<div
    class="relative z-[399]"
    [ngClass]="{ 'slate-opened': opened }"
>
    <aside
        class="slate-panel fixed inset-0 z-[399] flex h-[100dvh] w-full max-w-full flex-col border-0 bg-card shadow-none transition-transform duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] min-[768px]:inset-auto min-[768px]:right-0 min-[768px]:top-0 min-[768px]:bottom-0 min-[768px]:h-[100dvh] min-[768px]:w-[min(100vw,420px)] min-[768px]:border-l min-[768px]:border-slate-300/40 min-[768px]:shadow-[-12px_0_40px_rgba(15,23,42,0.12)] dark:bg-slate-900"
        [attr.aria-hidden]="!opened"
    >
        <header
            class="flex min-h-14 shrink-0 items-center justify-between gap-3 border-b border-slate-300/25 bg-gradient-to-r from-primary/15 to-transparent px-4 py-3 min-[768px]:min-h-16 min-[768px]:px-3.5 min-[768px]:py-3.5"
        >
            <div class="flex min-w-0 items-center gap-3">
                <div
                    class="slate-mark relative flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-primary-800 text-white shadow-md shadow-primary/40"
                    aria-hidden="true"
                >
                    <app-ai-icon></app-ai-icon>
                    <span class="slate-mark-pulse pointer-events-none absolute -inset-[3px] rounded-[inherit] border-2 border-primary/45"></span>
                </div>
                <div class="min-w-0">
                    <div class="text-base font-bold leading-tight tracking-tight min-[768px]:text-[0.95rem]">
                        {{ 'askTalkie.title' | translate }}
                    </div>
                    <div class="text-[0.7rem] text-slate-500 dark:text-slate-400">
                        {{ roleSubtitle || ('askTalkie.subtitle' | translate) }}
                    </div>
                </div>
            </div>
            <div class="flex items-center gap-0.5">
                <button
                    type="button"
                    class="rounded-md px-2 py-1 text-[0.65rem] font-bold uppercase tracking-wide text-primary-800 hover:bg-primary/10"
                    (click)="toggleDetailMode()"
                    [matTooltip]="'Toggle short / detailed answers'"
                >
                    {{ detailMode === 'short' ? 'Short' : 'Detail' }}
                </button>
                <button
                    mat-icon-button
                    matTooltip="Saved prompts"
                    (click)="showPalette = !showPalette"
                >
                    <mat-icon [svgIcon]="'heroicons_outline:bookmark'"></mat-icon>
                </button>
                <button
                    mat-icon-button
                    [matTooltip]="'askTalkie.clearChat' | translate"
                    (click)="clearChat()"
                    [disabled]="!messages.length"
                >
                    <mat-icon [svgIcon]="'heroicons_outline:trash'"></mat-icon>
                </button>
                <button
                    mat-icon-button
                    [matTooltip]="'askTalkie.close' | translate"
                    (click)="close()"
                >
                    <mat-icon [svgIcon]="'heroicons_outline:x-mark'"></mat-icon>
                </button>
            </div>
        </header>

        @if (stickyBits.length) {
            <div
                class="flex flex-wrap items-center gap-2 border-b border-slate-200/60 bg-primary/5 px-3 py-2 text-[0.7rem] dark:border-slate-700"
            >
                @for (bit of stickyBits; track bit) {
                    <span
                        class="rounded-full bg-card px-2 py-0.5 font-medium text-primary-800 dark:bg-slate-800"
                        >{{ bit }}</span
                    >
                }
                <button
                    type="button"
                    class="ml-auto text-xs font-semibold text-primary underline"
                    (click)="clearSticky()"
                >
                    Clear filters
                </button>
            </div>
        }

        @if (slowHint) {
            <div
                class="border-b border-amber-300/40 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-950/40 dark:text-amber-100"
            >
                Still loading live Studio data… You can wait, or ask Help and
                support if this keeps hanging.
            </div>
        }

        @if (showPalette) {
            <div
                class="max-h-40 overflow-y-auto border-b border-slate-200/70 bg-card px-3 py-2 dark:bg-slate-900"
            >
                <div class="mb-1 text-[0.65rem] font-semibold uppercase text-slate-500">
                    Prompts
                </div>
                <div class="flex flex-wrap gap-1.5">
                    @for (s of favorites.length ? favorites : suggestions; track s) {
                        <button
                            type="button"
                            class="rounded-full border border-primary/30 px-2.5 py-1 text-left text-[0.7rem] hover:bg-primary/10"
                            (click)="useSuggestion(s)"
                        >
                            {{ s }}
                        </button>
                    }
                </div>
            </div>
        }

        <div
            class="slate-thread flex flex-1 flex-col gap-3.5 overflow-y-auto overscroll-contain bg-default px-3.5 pb-4 pt-3.5 dark:bg-slate-950"
            #thread
        >
            @if (!messages.length) {
                <div class="animate-[slate-fade_360ms_ease_both] px-2 pb-4 pt-6 text-center">
                    <div
                        class="slate-mark slate-mark-lg relative mx-auto mb-3.5 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-primary to-primary-800 text-white shadow-lg shadow-primary/40"
                        aria-hidden="true"
                    >
                        <app-ai-icon></app-ai-icon>
                        <span class="slate-mark-pulse pointer-events-none absolute -inset-[3px] rounded-[inherit] border-2 border-primary/45"></span>
                    </div>
                    <p
                        class="mx-auto mb-2 w-fit rounded-full bg-primary/15 px-2.5 py-0.5 text-[0.68rem] font-semibold uppercase tracking-wide text-primary-800"
                    >
                        {{ 'askTalkie.badge' | translate }}
                    </p>
                    <h2 class="mb-1.5 text-xl font-bold">
                        {{ 'askTalkie.title' | translate }}
                    </h2>
                    <p class="mb-4 text-sm text-slate-500 dark:text-slate-400">
                        {{ 'askTalkie.emptyBody' | translate }}
                    </p>
                    <div class="flex flex-wrap justify-center gap-2">
                        @for (s of suggestions; track s) {
                            <div class="inline-flex max-w-full items-stretch gap-0.5">
                                <button
                                    type="button"
                                    class="rounded-l-full border border-primary/35 bg-card px-3 py-1.5 text-left text-xs font-medium text-primary-800 transition hover:bg-primary/10 dark:bg-slate-800 dark:text-primary-200"
                                    (click)="useSuggestion(s)"
                                >
                                    {{ s }}
                                </button>
                                <button
                                    type="button"
                                    class="rounded-r-full border border-l-0 border-primary/35 bg-card px-2 text-xs text-primary-800 hover:bg-primary/10 dark:bg-slate-800"
                                    (click)="toggleFavorite(s)"
                                    [matTooltip]="
                                        favorites.includes(s)
                                            ? 'Remove saved'
                                            : 'Save prompt'
                                    "
                                >
                                    {{ favorites.includes(s) ? '★' : '☆' }}
                                </button>
                            </div>
                        }
                    </div>
                    <p class="mt-4 text-[0.7rem] text-slate-500">
                        Wrong numbers or unclear answer? Ask
                        <button
                            type="button"
                            class="font-semibold text-primary underline"
                            (click)="useSuggestion('Help and support')"
                        >
                            Help and support
                        </button>
                        (email · WhatsApp · call).
                    </p>
                </div>
            }

            @for (msg of messages; track trackMsg($index, msg)) {
                <div
                    class="flex animate-[slate-fade_280ms_ease_both] gap-2"
                    [ngClass]="
                        msg.role === 'user' ? 'justify-end' : 'justify-start'
                    "
                >
                    @if (msg.role === 'assistant') {
                        <div
                            class="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary-800 text-white shadow-md shadow-primary/30"
                            aria-hidden="true"
                        >
                            <app-ai-icon></app-ai-icon>
                        </div>
                    }
                    <div
                        class="flex max-w-full flex-col gap-1.5 min-[768px]:max-w-[92%]"
                        [ngClass]="{
                            'max-w-[88%] rounded-2xl rounded-br-sm bg-primary px-3.5 py-2.5 text-white':
                                msg.role === 'user',
                        }"
                    >
                        @if (msg.text) {
                            <div
                                [ngClass]="
                                    msg.role === 'user'
                                        ? 'text-sm leading-relaxed'
                                        : 'rounded-2xl rounded-tl-sm border border-slate-300/25 bg-card px-3.5 py-2.5 text-sm leading-relaxed dark:bg-slate-800'
                                "
                            >
                                {{ msg.text }}
                            </div>
                        }

                        @if (msg.thinking?.length) {
                            <details
                                class="rounded-xl border border-dashed border-primary/35 bg-primary/5 px-2.5 py-1.5 text-xs text-slate-700 dark:text-slate-300"
                                [open]="!msg.thinkingDone"
                            >
                                <summary
                                    class="flex min-h-8 cursor-pointer items-center font-semibold text-primary-800 dark:text-primary-300"
                                >
                                    {{
                                        (msg.thinkingDone
                                            ? 'askTalkie.answered'
                                            : 'askTalkie.thinking'
                                        ) | translate
                                    }}
                                </summary>
                                <ul class="mt-1.5 list-disc pl-4">
                                    @for (step of msg.thinking; track step) {
                                        <li class="my-0.5">{{ step }}</li>
                                    }
                                </ul>
                            </details>
                        }

                        @if (msg.pending && !msg.cards?.length) {
                            <div class="space-y-1.5" aria-hidden="true">
                                <div class="slate-shimmer h-2.5 w-full rounded-full"></div>
                                <div class="slate-shimmer h-2.5 w-4/5 rounded-full"></div>
                                <div class="slate-shimmer h-2.5 w-3/5 rounded-full"></div>
                            </div>
                        }

                        @if (msg.cards?.length) {
                            @for (card of msg.cards; track $index) {
                                <div
                                    class="rounded-xl border border-slate-300/30 bg-card p-3.5 shadow-sm dark:border-slate-600/30 dark:bg-slate-800"
                                    [ngClass]="{
                                        'border-red-400/40 bg-red-50/90 dark:bg-red-950/40':
                                            card.type === 'refuse' ||
                                            card.type === 'error',
                                    }"
                                    [attr.data-type]="card.type"
                                >
                                    @if (card.live) {
                                        <div
                                            class="mb-1.5 inline-flex items-center gap-1.5 text-[0.65rem] font-bold uppercase tracking-wide text-primary-800"
                                        >
                                            <span
                                                class="h-1.5 w-1.5 rounded-full bg-primary shadow-[0_0_0_3px_rgba(20,163,141,0.25)]"
                                            ></span>
                                            {{ 'askTalkie.live' | translate }}
                                        </div>
                                    }
                                    @if (card.title) {
                                        <div
                                            class="mb-1 flex items-start justify-between gap-2"
                                        >
                                            <div class="text-[0.95rem] font-bold">
                                                {{ card.title }}
                                            </div>
                                            <button
                                                type="button"
                                                class="shrink-0 text-[0.65rem] font-semibold text-primary underline"
                                                (click)="copyCard(card, $event)"
                                            >
                                                Copy
                                            </button>
                                        </div>
                                    }
                                    @if (card.filterSummary) {
                                        <div class="mb-2 text-xs text-slate-500">
                                            {{ card.filterSummary }}
                                        </div>
                                    }
                                    @if (card.body) {
                                        <div class="mb-2 text-xs text-slate-500 dark:text-slate-400">
                                            {{ card.body }}
                                        </div>
                                    }
                                    @if (card.kpis?.length) {
                                        <div
                                            class="grid grid-cols-1 gap-2 min-[380px]:grid-cols-2"
                                        >
                                            @for (k of card.kpis; track k.label) {
                                                <div
                                                    class="relative rounded-lg bg-primary/10 px-2.5 py-2.5"
                                                    [class.pr-11]="k.copyable"
                                                >
                                                    <div
                                                        class="break-words text-[0.95rem] font-bold"
                                                    >
                                                        {{ k.value }}
                                                    </div>
                                                    <div
                                                        class="text-[0.7rem] text-slate-500"
                                                    >
                                                        {{ k.label }}
                                                    </div>
                                                    @if (
                                                        k.copyable &&
                                                        k.value != null &&
                                                        k.value !== '—'
                                                    ) {
                                                        <button
                                                            type="button"
                                                            class="absolute right-1.5 top-1.5 min-h-7 rounded border border-primary/35 bg-card px-1.5 py-0.5 text-[0.65rem] font-semibold text-primary-700 hover:bg-primary/10"
                                                            (click)="
                                                                copyText(
                                                                    '' + k.value,
                                                                    $event
                                                                )
                                                            "
                                                            [matTooltip]="
                                                                ('askTalkie.copy'
                                                                    | translate) +
                                                                ' ' +
                                                                k.label
                                                            "
                                                        >
                                                            {{
                                                                'askTalkie.copy'
                                                                    | translate
                                                            }}
                                                        </button>
                                                    }
                                                </div>
                                            }
                                        </div>
                                    }
                                    @if (card.spark?.length) {
                                        <div
                                            class="mt-2 flex h-8 items-end gap-0.5"
                                            aria-hidden="true"
                                        >
                                            @for (v of card.spark; track $index) {
                                                <span
                                                    class="min-w-[4px] flex-1 rounded-t bg-primary/70"
                                                    [style.height.%]="v"
                                                ></span>
                                            }
                                        </div>
                                    }
                                    @if (card.columns?.length && card.rows?.length) {
                                        <div
                                            class="overflow-x-auto rounded-lg border border-slate-300/25"
                                        >
                                            <table class="w-full min-w-[16rem] text-left text-xs">
                                                <thead class="bg-slate-50 dark:bg-slate-900/60">
                                                    <tr>
                                                        @for (
                                                            col of card.columns;
                                                            track col.key
                                                        ) {
                                                            <th
                                                                class="whitespace-nowrap px-2 py-1.5 font-semibold"
                                                            >
                                                                {{ col.label }}
                                                            </th>
                                                        }
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    @for (
                                                        row of card.rows;
                                                        track $index
                                                    ) {
                                                        <tr
                                                            class="border-t border-slate-200/60 dark:border-slate-700/50"
                                                        >
                                                            @for (
                                                                col of card.columns;
                                                                track col.key
                                                            ) {
                                                                <td class="px-2 py-1.5">
                                                                    {{
                                                                        row[col.key]
                                                                    }}
                                                                </td>
                                                            }
                                                        </tr>
                                                    }
                                                </tbody>
                                            </table>
                                        </div>
                                    }
                                    @if (card.steps?.length) {
                                        <ol
                                            class="mt-1 list-decimal space-y-1 pl-4 text-xs text-slate-600 dark:text-slate-300"
                                        >
                                            @for (step of card.steps; track step) {
                                                <li>{{ step }}</li>
                                            }
                                        </ol>
                                    }
                                    @if (card.links?.length) {
                                        <div class="mt-2 flex flex-wrap gap-2">
                                            @for (link of card.links; track link.label) {
                                                @if (link.copyText) {
                                                    <button
                                                        type="button"
                                                        class="rounded-lg border border-primary/40 bg-primary/10 px-3 py-1.5 text-xs font-semibold text-primary-800 hover:bg-primary/20"
                                                        (click)="
                                                            copyText(
                                                                link.copyText,
                                                                $event
                                                            )
                                                        "
                                                    >
                                                        {{ link.label }}
                                                    </button>
                                                } @else {
                                                    <button
                                                        type="button"
                                                        class="rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary-700"
                                                        (click)="
                                                            openLink(
                                                                link.route,
                                                                link.queryParams
                                                            )
                                                        "
                                                    >
                                                        {{ link.label }}
                                                    </button>
                                                }
                                            }
                                        </div>
                                    }
                                    @if (card.chips?.length) {
                                        <div class="mt-2 flex flex-wrap gap-2">
                                            @for (chip of card.chips; track chipText(chip)) {
                                                <button
                                                    type="button"
                                                    class="rounded-full border border-primary/35 bg-card px-3 py-1.5 text-left text-xs font-medium text-primary-800 transition hover:bg-primary/10 dark:bg-slate-900 dark:text-primary-200"
                                                    (click)="onChip(chip, card)"
                                                >
                                                    {{ chipText(chip) }}
                                                </button>
                                            }
                                        </div>
                                    }
                                </div>
                            }
                        }
                    </div>
                </div>
            }
        </div>

        <footer
            class="flex shrink-0 items-end gap-2 border-t border-slate-300/25 bg-card px-3 py-3 dark:bg-slate-900"
        >
            <textarea
                #inputEl
                rows="1"
                class="max-h-28 min-h-11 flex-1 resize-none rounded-xl border border-slate-300/40 bg-transparent px-3.5 py-2.5 text-base outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                [(ngModel)]="draft"
                [disabled]="busy"
                [placeholder]="'askTalkie.placeholder' | translate"
                (keydown.enter)="onEnter($event)"
            ></textarea>
            <button
                mat-mini-fab
                color="primary"
                [disabled]="busy || !draft.trim()"
                (click)="send()"
                [attr.aria-label]="'askTalkie.send' | translate"
            >
                <mat-icon [svgIcon]="'heroicons_outline:paper-airplane'"></mat-icon>
            </button>
        </footer>
    </aside>
</div>
